import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Folder, CheckSquare, Briefcase, User, FileText, BookOpen, X, Loader2 } from "lucide-react";

const TYPE_META = {
  project: { icon: Folder,      label: "Project",  accent: "text-[#00F5D4]" },
  task:    { icon: CheckSquare, label: "Task",     accent: "text-[#002FA7]" },
  deal:    { icon: Briefcase,   label: "Deal",     accent: "text-[#F59E0B]" },
  contact: { icon: User,        label: "Contact",  accent: "text-[#9CA3AF]" },
  invoice: { icon: FileText,    label: "Invoice",  accent: "text-[#FF007A]" },
  wiki:    { icon: BookOpen,    label: "Wiki",     accent: "text-purple-400" },
};

const HEALTH_DOT = { green: "bg-[#00F5D4]", amber: "bg-[#F59E0B]", red: "bg-[#FF007A]" };

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const search = useCallback(async (q) => {
    // Bumping this invalidates any still-in-flight request, so a slower
    // earlier response can't clobber the results of a newer query.
    const reqId = ++requestIdRef.current;
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
      if (reqId !== requestIdRef.current) return;
      setResults(data.results || []);
      setActive(0);
    } catch {
      if (reqId === requestIdRef.current) setResults([]);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, []);

  // Debounce 250 ms
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(timerRef.current);
  }, [query, search]);

  const pick = (result) => {
    navigate(result.url);
    onClose();
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && results[active]) { pick(results[active]); }
    if (e.key === "Escape") { onClose(); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative w-full max-w-xl bg-[#0D1117] border border-[#374151] shadow-2xl" data-testid="command-palette">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1F2937]">
          {loading
            ? <Loader2 className="w-4 h-4 text-[#00F5D4] shrink-0 animate-spin" />
            : <Search className="w-4 h-4 text-[#6B7280] shrink-0" />
          }
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search projects, tasks, deals, contacts…"
            className="flex-1 bg-transparent text-sm text-white placeholder:text-[#4B5563] outline-none"
            data-testid="command-palette-input"
          />
          {query && (
            <button onClick={() => { setQuery(""); setResults([]); inputRef.current?.focus(); }} className="text-[#4B5563] hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <kbd className="hidden sm:block text-[10px] font-mono text-[#4B5563] border border-[#374151] px-1.5 py-0.5">ESC</kbd>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <ul className="max-h-[380px] overflow-y-auto py-1" data-testid="command-results">
            {results.map((r, i) => {
              const meta = TYPE_META[r.type] || TYPE_META.task;
              const Icon = meta.icon;
              return (
                <li key={`${r.type}-${r.id}`}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(r)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === active ? "bg-[#1F2937]" : "hover:bg-[#111827]"}`}
                    data-testid={`cmd-result-${r.type}-${r.id}`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${meta.accent}`} strokeWidth={1.7} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-2">
                        {r.health && <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${HEALTH_DOT[r.health] || "bg-gray-500"}`} />}
                        {r.title}
                      </div>
                      {r.subtitle && <div className="text-[11px] text-[#6B7280] truncate capitalize">{r.subtitle}</div>}
                    </div>
                    <span className={`text-[9px] uppercase tracking-[0.2em] font-mono shrink-0 ${meta.accent}`}>{meta.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {query && !loading && results.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#4B5563]" data-testid="no-results">
            No results for <span className="text-white">"{query}"</span>
          </div>
        )}

        {!query && (
          <div className="px-4 py-6 text-center text-xs text-[#4B5563]">
            Type to search across all your projects, tasks, deals and more.
          </div>
        )}

        {/* Footer hint */}
        <div className="border-t border-[#1F2937] px-4 py-2 flex items-center gap-4 text-[10px] text-[#4B5563] font-mono">
          <span><kbd className="border border-[#374151] px-1">↑↓</kbd> navigate</span>
          <span><kbd className="border border-[#374151] px-1">↵</kbd> open</span>
          <span><kbd className="border border-[#374151] px-1">Cmd K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
